import { useState, useRef, useEffect } from 'react';
import { Avatar } from 'primereact/avatar';
import { api } from '../api';
import { config } from '../config';
import { User } from '../types';
import { getManageAccountUrl } from '../utils/manage-account-url';

interface UserMenuProps {
  user: User;
  onLogout: () => void;
}

export function UserMenu({ user, onLogout }: UserMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [imageError, setImageError] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Get user initials for fallback
  const getInitials = (name: string | undefined, email: string): string => {
    if (name) {
      const parts = name.split(' ');
      if (parts.length >= 2) {
        return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    }
    return email.substring(0, 2).toUpperCase();
  };

  // Generate consistent color from email
  const getAvatarColor = (email: string): string => {
    const colors = [
      '#164293', // ETAS blue
      '#89037a', // ETAS purple
      '#0066cc', // Blue
      '#006633', // Green
      '#cc6600', // Orange
      '#663399', // Purple
      '#cc0066', // Pink
      '#009999', // Teal
    ];
    
    let hash = 0;
    for (let i = 0; i < email.length; i++) {
      hash = email.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    return colors[Math.abs(hash) % colors.length];
  };

  const handleLogout = () => {
    setIsOpen(false);
    onLogout();
  };

  const handleManageAccount = () => {
    setIsOpen(false);
    window.open(getManageAccountUrl(config.cloudHub.docsBaseUrl), '_blank', 'noopener,noreferrer');
  };

  const [userImageUrl, setUserImageUrl] = useState<string | null>(null);
  const initials = getInitials(user.name, user.email);
  const avatarColor = getAvatarColor(user.email);

  // Fetch user image on mount
  useEffect(() => {
    api.getUserImage(user.id)
      .then((imageUrl) => {
        setUserImageUrl(imageUrl);
      })
      .catch((error) => {
        console.error('Failed to fetch user image:', error);
        setUserImageUrl(null);
      });
  }, [user.id]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div style={{ position: 'relative' }} ref={menuRef}>
      <div
        onClick={() => setIsOpen(!isOpen)}
        style={{
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {userImageUrl && !imageError ? (
          <Avatar
            image={userImageUrl}
            label={initials}
            size="normal"
            shape="circle"
            style={{
              backgroundColor: avatarColor,
              color: 'white',
            }}
            onImageError={() => setImageError(true)}
          />
        ) : (
          <Avatar
            label={initials}
            size="normal"
            shape="circle"
            style={{
              backgroundColor: avatarColor,
              color: 'white',
            }}
          />
        )}
      </div>

      {isOpen && (
        <div
          style={{
            position: 'absolute',
            right: 0,
            top: 'calc(100% + 8px)',
            width: '246px',
            backgroundColor: '#FFFFFF',
            border: '1px solid #CDD0D3',
            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.14), 0px 0px 2px rgba(0, 0, 0, 0.12)',
            borderRadius: '6px',
            display: 'flex',
            flexDirection: 'column',
            zIndex: 1000,
          }}
        >
          {/* Menu Items */}
          <div style={{ padding: '8px 0' }}>
            <div
              onClick={handleManageAccount}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '8px 16px',
                gap: '8px',
                cursor: 'pointer',
                transition: 'background-color 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#E8ECF4';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <i className="ph ph-gear" style={{ fontSize: '20px', color: '#164293' }}></i>
              <span style={{ fontWeight: 600, fontSize: '14px', color: '#164293' }}>
                Manage account
              </span>
            </div>

            <div
              onClick={handleLogout}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '8px 16px',
                gap: '8px',
                cursor: 'pointer',
                transition: 'background-color 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#E8ECF4';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <i className="cui-icon cui-icon-sign-out" style={{ fontSize: '20px', color: '#164293' }}></i>
              <span style={{ fontWeight: 600, fontSize: '14px', color: '#164293' }}>
                Sign out
              </span>
            </div>
          </div>

          {/* User Info Footer */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '16px',
              gap: '8px',
              backgroundColor: '#FAFAFA',
              borderTop: '1px solid #EEEFF0',
              borderBottomLeftRadius: '6px',
              borderBottomRightRadius: '6px',
            }}
          >
            {/* Avatar */}
            <div style={{ flexShrink: 0 }}>
              {userImageUrl && !imageError ? (
                <Avatar
                  image={userImageUrl}
                  label={initials}
                  size="normal"
                  shape="circle"
                  style={{
                    backgroundColor: avatarColor,
                    color: 'white',
                    width: '32px',
                    height: '32px',
                    fontSize: '12px',
                  }}
                  onImageError={() => setImageError(true)}
                />
              ) : (
                <Avatar
                  label={initials}
                  size="normal"
                  shape="circle"
                  style={{
                    backgroundColor: avatarColor,
                    color: 'white',
                    width: '32px',
                    height: '32px',
                    fontSize: '12px',
                  }}
                />
              )}
            </div>

            {/* User Info */}
            <div style={{ flex: 1, overflow: 'hidden' }}>
              <div
                style={{
                  fontWeight: 600,
                  fontSize: '16px',
                  lineHeight: '24px',
                  color: '#000000',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {user.name || 'User'}
              </div>
              <div
                style={{
                  fontWeight: 400,
                  fontSize: '12px',
                  lineHeight: '20px',
                  color: '#000000',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {user.email}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
